import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { DialogContent, TextField, Button, InputLabel, Box, Typography, Select, MenuItem, Paper, IconButton } from '@mui/material';
import { AddCircleOutline, Delete, Edit, OpenInNew } from '@mui/icons-material';
import '@uploadcare/react-uploader/core.css';

import AvatarUploadField from 'src/pages/Experts/components/AvatarUploadField';

const requiredFields = ['name', 'position', 'company', 'bio'];

const ExpertModalForm = forwardRef(
  (
    {
      formData,
      onChange,
      onAddPoC,
      onRemovePoC,
      onUpdatePoC,
      onAddReportContact,
      onRemoveReportContact,
      onUpdateReportContact,
      editMode = true,
      onValidationChange,
    },
    ref
  ) => {
    const isFormValid = React.useCallback(() => {
      // Check all required fields except email
      const basicFieldsValid = requiredFields.every((field) => formData[field] && formData[field].trim() !== '');

      // Check if email is filled
      const hasEmail = formData.email && formData.email.trim() !== '';

      // Check if at least one valid PoC exists (with all fields filled)
      const hasValidPoC =
        formData.pointOfContactJson &&
        formData.pointOfContactJson.some(
          (poc) => poc.firstName && poc.firstName.trim() !== '' && poc.lastName && poc.lastName.trim() !== '' && poc.email && poc.email.trim() !== ''
        );

      // Form is valid if basic fields are filled AND either email or valid PoC exists
      return basicFieldsValid && (hasEmail || hasValidPoC);
    }, [formData]);

    React.useEffect(() => {
      if (onValidationChange) {
        onValidationChange(isFormValid());
      }
    }, [formData, onValidationChange, isFormValid]);

    const getPlaceholder = (field) => {
      const placeholders = {
        name: 'Enter expert name',
        email: 'Enter expert email',
        position: 'Enter expert position',
        company: 'Enter company name',
        bio: 'Enter expert bio',
        hashtags: 'Enter hashtags (comma separated)',
        promptModify: 'Enter custom prompt',
        qwotedURL: 'Enter Qwoted profile URL',
        companyURL: 'Enter company website URL',
        linkedinURL: 'Enter LinkedIn profile URL',
        twitterURL: 'Enter Twitter profile URL',
        websiteURL: 'Enter personal website URL',
        imageURL: 'Enter profile image URL',
      };
      return placeholders[field] || '';
    };

    const renderField = (label, field, multiline = false) => {
      const isRequired = requiredFields.includes(field);
      const isConditionallyRequired = field === 'email';

      return (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <InputLabel
              sx={{
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              {label}
              {isRequired && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              {isConditionallyRequired && <span style={{ color: 'orange', marginLeft: '4px' }}>*</span>}
            </InputLabel>
            {!editMode && !formData.editStates?.[field] && (
              <IconButton
                size="small"
                onClick={() => onChange('editStates', { ...formData.editStates, [field]: true })}
                sx={{ color: 'primary.main' }}
              >
                <Edit />
              </IconButton>
            )}
          </Box>
          <TextField
            fullWidth
            multiline={multiline}
            rows={multiline ? 4 : 1}
            variant="outlined"
            value={formData[field]}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={!editMode && !formData.editStates?.[field]}
            size="small"
            placeholder={getPlaceholder(field)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: formData.editStates?.[field] ? '#fff3cd' : '#fff',
                '&.Mui-disabled': {
                  backgroundColor: '#f8fafc',
                },
                ...(formData.editStates?.[field] && {
                  '& fieldset': {
                    borderColor: '#ffc107',
                    borderWidth: '2px',
                  },
                }),
              },
            }}
          />
        </Box>
      );
    };

    const renderURLField = (label, field) => (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <InputLabel
            sx={{
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            {label}
          </InputLabel>
          {!editMode && !formData.editStates?.[field] && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => onChange('editStates', { ...formData.editStates, [field]: true })}
                sx={{ color: 'primary.main' }}
              >
                <Edit />
              </IconButton>
              {formData[field] && (
                <IconButton size="small" href={formData[field]} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                  <OpenInNew />
                </IconButton>
              )}
            </Box>
          )}
          {!editMode && formData.editStates?.[field] && formData[field] && (
            <IconButton size="small" href={formData[field]} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
              <OpenInNew />
            </IconButton>
          )}
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          value={formData[field]}
          onChange={(e) => onChange(field, e.target.value)}
          disabled={!editMode && !formData.editStates?.[field]}
          size="small"
          placeholder={getPlaceholder(field)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: formData.editStates?.[field] ? '#fff3cd' : '#fff',
              '&.Mui-disabled': {
                backgroundColor: '#f8fafc',
              },
              ...(formData.editStates?.[field] && {
                '& fieldset': {
                  borderColor: '#ffc107',
                  borderWidth: '2px',
                },
              }),
            },
          }}
        />
      </Box>
    );

    const renderLocationField = () => (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <InputLabel
            sx={{
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            Location
          </InputLabel>
          {!editMode && !formData.editStates?.location && (
            <IconButton
              size="small"
              onClick={() => onChange('editStates', { ...formData.editStates, location: true })}
              sx={{ color: 'primary.main' }}
            >
              <Edit />
            </IconButton>
          )}
        </Box>
        <Select
          fullWidth
          value={formData.location}
          onChange={(e) => onChange('location', e.target.value)}
          disabled={!editMode && !formData.editStates?.location}
          size="small"
          sx={{
            height: '38px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: formData.editStates?.location ? '#fff3cd' : '#fff',
              '&.Mui-disabled': {
                backgroundColor: '#f8fafc',
              },
              ...(formData.editStates?.location && {
                '& fieldset': {
                  borderColor: '#ffc107',
                  borderWidth: '2px',
                },
              }),
            },
            '& .MuiSelect-select': {
              backgroundColor: formData.editStates?.location ? '#fff3cd' : '#f8fafc',
            },
          }}
        >
          <MenuItem value="Location not specified">Location not specified</MenuItem>
          <MenuItem value="US">US</MenuItem>
          <MenuItem value="Canada">Canada</MenuItem>
          <MenuItem value="US and Canada">US and Canada</MenuItem>
        </Select>
      </Box>
    );

    return (
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Basic Information Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Basic Information
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 3 },
              mb: 4,
              backgroundColor: '#fff',
              borderRadius: { xs: 0, sm: '12px' },
              border: { xs: 0, sm: '1px solid rgba(0,0,0,0.08)' },
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <Box>
                <AvatarUploadField ref={ref} onChange={onChange} value={formData.imageURL} editMode={editMode} />

                {renderField('Name', 'name')}
                <Box>
                  {renderField('Email', 'email')}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1, display: 'block', fontSize: '0.75rem' }}>
                    * Either email or at least one point of contact is required
                  </Typography>
                </Box>
              </Box>
              <Box>
                {renderField('Position', 'position')}
                {renderLocationField()}
                {renderField('Company', 'company')}
              </Box>
            </Box>
          </Paper>

          {/* Additional Details Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Additional Details
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 3 },
              mb: 4,
              backgroundColor: '#fff',
              borderRadius: { xs: 0, sm: '12px' },
              border: { xs: 0, sm: '1px solid rgba(0,0,0,0.08)' },
            }}
          >
            {renderField('Bio', 'bio', true)}
            {renderField('Hashtags', 'hashtags')}
          </Paper>

          {/* Custom Prompt Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Custom Prompt
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 0, sm: 3 },
              mb: 4,
              backgroundColor: '#fff',
              borderRadius: { xs: 0, sm: '12px' },
              border: { xs: 0, sm: '1px solid rgba(0,0,0,0.08)' },
            }}
          >
            {renderField('Custom Prompt', 'promptModify', true)}
          </Paper>

          {/* Points of Contact Section */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                display: 'inline-block',
              }}
            >
              Points of Contact
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2, fontSize: '0.875rem' }}>
              * Either email or at least one complete contact is required
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: formData.editStates?.pointOfContactJson ? '2px solid #ffc107' : '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Contact List
              </Typography>
              {!editMode && !formData.editStates?.pointOfContactJson && (
                <Button
                  startIcon={<Edit />}
                  onClick={() => onChange('editStates', { ...formData.editStates, pointOfContactJson: true })}
                  variant="text"
                  size="small"
                >
                  Edit Contacts
                </Button>
              )}
            </Box>

            <Box sx={{ p: 2 }}>
              {/* Table Header */}
              {formData.pointOfContactJson?.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 2fr',
                    gap: '16px',
                    mb: 2,
                    px: 1,
                    py: 1,
                    backgroundColor: formData.editStates?.pointOfContactJson ? '#fff3cd' : 'transparent',
                    borderRadius: '8px',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    First Name
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    Last Name
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    Email
                  </Typography>
                  <Box />
                </Box>
              )}

              {/* Table Body */}
              {formData.pointOfContactJson?.map((poc) => (
                <Box
                  key={poc.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 2fr',
                    gap: '16px',
                    p: 1,
                    '&:hover': {
                      backgroundColor: editMode || formData.editStates?.pointOfContactJson ? 'rgba(0,0,0,0.02)' : 'transparent',
                    },
                    borderRadius: '8px',
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    value={poc.firstName}
                    onChange={(e) => onUpdatePoC(poc.id, 'firstName', e.target.value)}
                    disabled={!editMode && !formData.editStates?.pointOfContactJson}
                    size="small"
                    placeholder="Enter first name"
                    variant={editMode || formData.editStates?.pointOfContactJson ? 'outlined' : 'standard'}
                    InputProps={{
                      sx: {
                        backgroundColor: editMode || formData.editStates?.pointOfContactJson ? '#f8fafc' : 'transparent',
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                      },
                    }}
                  />
                  <TextField
                    value={poc.lastName}
                    onChange={(e) => onUpdatePoC(poc.id, 'lastName', e.target.value)}
                    disabled={!editMode && !formData.editStates?.pointOfContactJson}
                    size="small"
                    placeholder="Enter last name"
                    variant={editMode || formData.editStates?.pointOfContactJson ? 'outlined' : 'standard'}
                    InputProps={{
                      sx: {
                        backgroundColor: editMode || formData.editStates?.pointOfContactJson ? '#f8fafc' : 'transparent',
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                      },
                    }}
                  />
                  <TextField
                    value={poc.email}
                    onChange={(e) => onUpdatePoC(poc.id, 'email', e.target.value)}
                    disabled={!editMode && !formData.editStates?.pointOfContactJson}
                    size="small"
                    placeholder="Enter email address"
                    variant={editMode || formData.editStates?.pointOfContactJson ? 'outlined' : 'standard'}
                    InputProps={{
                      sx: {
                        backgroundColor: editMode || formData.editStates?.pointOfContactJson ? '#f8fafc' : 'transparent',
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                      },
                    }}
                  />
                  {(editMode || formData.editStates?.pointOfContactJson) && (
                    <IconButton
                      onClick={() => onRemovePoC(poc.id)}
                      sx={{
                        color: 'error.main',
                        width: 40,
                        height: 40,
                        '&:hover': {
                          backgroundColor: 'error.lighter',
                        },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}

              {/* Empty State */}
              {(!formData.pointOfContactJson || formData.pointOfContactJson.length === 0) && (
                <Box
                  sx={{
                    py: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">No contacts added yet</Typography>
                </Box>
              )}

              {/* Add Contact Button */}
              {(editMode || formData.editStates?.pointOfContactJson) && (
                <Button startIcon={<AddCircleOutline />} onClick={onAddPoC} variant="text" sx={{ mt: 2 }}>
                  Add Contact
                </Button>
              )}
            </Box>
          </Paper>

          {/* Report Contacts Section */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                display: 'inline-block',
              }}
            >
              Report Contacts
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: formData.editStates?.reportsContactsJson ? '2px solid #ffc107' : '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Report Contact List
              </Typography>
              {!editMode && !formData.editStates?.reportsContactsJson && (
                <Button
                  startIcon={<Edit />}
                  onClick={() => onChange('editStates', { ...formData.editStates, reportsContactsJson: true })}
                  variant="text"
                  size="small"
                >
                  Edit Report Contacts
                </Button>
              )}
            </Box>

            <Box sx={{ p: 2 }}>
              {/* Table Header */}
              {formData.reportsContactsJson?.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 2fr',
                    gap: '16px',
                    mb: 2,
                    px: 1,
                    py: 1,
                    backgroundColor: formData.editStates?.reportsContactsJson ? '#fff3cd' : 'transparent',
                    borderRadius: '8px',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    First Name
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    Last Name
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    Email
                  </Typography>
                  <Box />
                </Box>
              )}

              {/* Table Body */}
              {formData.reportsContactsJson?.map((contact) => (
                <Box
                  key={contact.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 2fr',
                    gap: '16px',
                    p: 1,
                    '&:hover': {
                      backgroundColor: editMode || formData.editStates?.reportsContactsJson ? 'rgba(0,0,0,0.02)' : 'transparent',
                    },
                    borderRadius: '8px',
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    value={contact.firstName}
                    onChange={(e) => onUpdateReportContact(contact.id, 'firstName', e.target.value)}
                    disabled={!editMode && !formData.editStates?.reportsContactsJson}
                    size="small"
                    placeholder="Enter first name"
                    variant={editMode || formData.editStates?.reportsContactsJson ? 'outlined' : 'standard'}
                    InputProps={{
                      sx: {
                        backgroundColor: editMode || formData.editStates?.reportsContactsJson ? '#f8fafc' : 'transparent',
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                      },
                    }}
                  />
                  <TextField
                    value={contact.lastName}
                    onChange={(e) => onUpdateReportContact(contact.id, 'lastName', e.target.value)}
                    disabled={!editMode && !formData.editStates?.reportsContactsJson}
                    size="small"
                    placeholder="Enter last name"
                    variant={editMode || formData.editStates?.reportsContactsJson ? 'outlined' : 'standard'}
                    InputProps={{
                      sx: {
                        backgroundColor: editMode || formData.editStates?.reportsContactsJson ? '#f8fafc' : 'transparent',
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                      },
                    }}
                  />
                  <TextField
                    value={contact.email}
                    onChange={(e) => onUpdateReportContact(contact.id, 'email', e.target.value)}
                    disabled={!editMode && !formData.editStates?.reportsContactsJson}
                    size="small"
                    placeholder="Enter email address"
                    variant={editMode || formData.editStates?.reportsContactsJson ? 'outlined' : 'standard'}
                    InputProps={{
                      sx: {
                        backgroundColor: editMode || formData.editStates?.reportsContactsJson ? '#f8fafc' : 'transparent',
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                      },
                    }}
                  />
                  {(editMode || formData.editStates?.reportsContactsJson) && (
                    <IconButton
                      onClick={() => onRemoveReportContact(contact.id)}
                      sx={{
                        color: 'error.main',
                        width: 40,
                        height: 40,
                        '&:hover': {
                          backgroundColor: 'error.lighter',
                        },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}

              {/* Empty State */}
              {(!formData.reportsContactsJson || formData.reportsContactsJson.length === 0) && (
                <Box
                  sx={{
                    py: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">No report contacts added yet</Typography>
                </Box>
              )}

              {/* Add Contact Button */}
              {(editMode || formData.editStates?.reportsContactsJson) && (
                <Button startIcon={<AddCircleOutline />} onClick={onAddReportContact} variant="text" sx={{ mt: 2 }}>
                  Add Report Contact
                </Button>
              )}
            </Box>
          </Paper>

          {/* URLs Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            URLs
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 3 },
              mb: 4,
              backgroundColor: '#fff',
              borderRadius: { xs: 0, sm: '12px' },
              border: { xs: 0, sm: '1px solid rgba(0,0,0,0.08)' },
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Box>
                {renderURLField('Qwoted URL', 'qwotedURL')}
                {renderURLField('Company URL', 'companyURL')}
                {renderURLField('LinkedIn URL', 'linkedinURL')}
              </Box>
              <Box>
                {renderURLField('Twitter URL', 'twitterURL')}
                {renderURLField('Website URL', 'websiteURL')}
              </Box>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
    );
  }
);

ExpertModalForm.displayName = 'ExpertModalForm';

ExpertModalForm.propTypes = {
  formData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    company: PropTypes.string.isRequired,
    bio: PropTypes.string.isRequired,
    hashtags: PropTypes.string.isRequired,
    pointOfContactJson: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      })
    ).isRequired,
    reportsContactsJson: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      })
    ).isRequired,
    promptModify: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    imageURL: PropTypes.string.isRequired,
    qwotedURL: PropTypes.string.isRequired,
    companyURL: PropTypes.string.isRequired,
    linkedinURL: PropTypes.string.isRequired,
    twitterURL: PropTypes.string.isRequired,
    websiteURL: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    editStates: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onAddPoC: PropTypes.func.isRequired,
  onRemovePoC: PropTypes.func.isRequired,
  onUpdatePoC: PropTypes.func.isRequired,
  onAddReportContact: PropTypes.func.isRequired,
  onRemoveReportContact: PropTypes.func.isRequired,
  onUpdateReportContact: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  onValidationChange: PropTypes.func,
};

ExpertModalForm.defaultProps = {
  editMode: true,
  onValidationChange: () => null,
};

export default ExpertModalForm;
